import {File02Icon, PlusIcon, SquareIcon} from '@/components/ui/icon/Icon'
import {PatientExtended} from '@/features/patients/types'
import {routes} from '@/utilities/constants/routes'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {StyledPatientDetailsCtaButtonLink} from './style'
import {treatmentRequestStatusToColors, treatmentRequestStatusToLabel} from '@/features/treatment-requests/utils'
import {generatePath} from 'react-router-dom'
import {projectStatusToColor, projectStatusToLabel} from '@/features/projects/utils.ts'
import {usePermissions} from '@/features/auth/store/store'

export const PatientDetailsOptionsCta = ({
    patient
}: {
    patient: PatientExtended & {
        treatmentRequestEligibility: NonNullable<PatientExtended['treatmentRequestEligibility']>
    }
}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const {treatmentRequestEligibility} = patient
    const error = treatmentRequestEligibility.error

    if (patient.project) {
        return (
            <StyledPatientDetailsCtaButtonLink
                variant="secondary"
                $isReadOnly={!permissions?.projects?.read}
                to={
                    permissions?.projects?.read
                        ? generatePath(routes.PROJECT_DETAILS_ROUTE.handle.path, {id: patient.project.id})
                        : ''
                }
            >
                <Flexbox render="span" align="center" gap={2}>
                    <File02Icon size={15} />
                    <b>{t('projects:pri_n', {code: patient.project.code})}</b>
                    {patient.project.status && (
                        <>
                            <SquareIcon
                                className="custom"
                                size={8}
                                color={projectStatusToColor[patient.project.status]}
                            />
                            {t(projectStatusToLabel[patient.project.status])}
                        </>
                    )}
                </Flexbox>
            </StyledPatientDetailsCtaButtonLink>
        )
    }

    if (permissions?.requests?.write && treatmentRequestEligibility.canRequest) {
        return (
            <StyledPatientDetailsCtaButtonLink
                variant="primaryNeutral"
                disabled={patient.status == 'deceased'}
                to={{
                    pathname: routes.CREATE_TREATMENT_REQUEST.handle.path,
                    search: `?patientId=${patient.id}`
                }}
            >
                <PlusIcon />
                {t('commons:treatment_request')}
            </StyledPatientDetailsCtaButtonLink>
        )
    }

    if (error?.code == 'request-already-exists' && patient.treatmentRequest?.status) {
        return (
            <StyledPatientDetailsCtaButtonLink
                $isReadOnly={!permissions?.requests?.read}
                variant="secondary"
                to={generatePath(routes.TREATMENT_REQUEST_DETAILS_ROUTE.handle.path, {
                    id: patient.treatmentRequest.id
                })}
            >
                <Flexbox render="span" align="center" gap={2}>
                    <File02Icon size={15} />
                    <b>{t('patients:request_n', {number: patient.treatmentRequest.code})}</b>
                    <SquareIcon
                        className="custom"
                        size={8}
                        color={treatmentRequestStatusToColors[patient.treatmentRequest.status]}
                    />
                    {t(treatmentRequestStatusToLabel[patient.treatmentRequest.status])}
                </Flexbox>
            </StyledPatientDetailsCtaButtonLink>
        )
    }

    return null
}
